import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, MoreHorizontal } from 'lucide-react';

const Navbar = ({ showTestimonials = false }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navLinks = useMemo(() => {
    const links = [
      { name: 'Home', id: 'home', type: 'page' },
      { name: 'Services', id: 'services', type: 'section' },
      { name: 'About', id: 'about', type: 'page' },
      { name: 'Contact', id: 'contact', type: 'section' },
    ];

    if (showTestimonials) {
      links.splice(-1, 0, { name: 'Testimonials', id: 'testimonials', type: 'section' });
    }

    return links;
  }, [showTestimonials]);

  const [visibleItems, setVisibleItems] = useState(navLinks);
  const [hiddenItems, setHiddenItems] = useState([]);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const container = navRef.current;
      if (!container) return;
      
      // Get the actual container width
      const containerWidth = container.offsetWidth;
      
      // Calculate space needed for each item (more conservative estimates)
      const itemWidth = 120; // More accurate estimate including padding
      
      // Calculate how many items can fit
      const maxItems = Math.floor(containerWidth / itemWidth);
      
      if (maxItems >= navLinks.length) {
        // All items can fit
        setVisibleItems(navLinks);
        setHiddenItems([]);
      } else if (maxItems <= 1) {
        // Only show dropdown
        setVisibleItems([]);
        setHiddenItems(navLinks);
      } else {
        // Show some items + dropdown
        const numVisible = maxItems - 1; // Reserve space for dropdown
        setVisibleItems(navLinks.slice(0, numVisible));
        setHiddenItems(navLinks.slice(numVisible));
      }
    };
    
    // Initial call with a slight delay to ensure DOM is ready
    setTimeout(handleResize, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navLinks]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (link) => {
    if (link.type === 'page') {
      if (link.id === 'home') {
        navigate('/');
        window.scrollTo(0, 0);
      } else if (link.id === 'about') {
        navigate('/about');
        window.scrollTo(0, 0);
      }
    } else {
      // Handle section scrolling
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(link.id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(link.id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsOpen(false);
    setShowDropdown(false);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center text-gray-900 hover:text-primary-600 transition-colors"
            >
              <img 
                src="/oldmanfavicon.png" 
                alt="GSCS Logo" 
                className="h-8 w-8 mr-3"
              />
              <span className="text-2xl font-bold">GSCS</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center flex-1 justify-center" ref={navRef}>
            <div className="flex items-baseline space-x-8">
              {visibleItems.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleNavClick(link)}
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  {link.name}
                </button>
              ))}
              {hiddenItems.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      {hiddenItems.map((link) => (
                        <button
                          key={link.id}
                          onClick={() => handleNavClick(link)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary-600 transition-colors duration-200"
                        >
                          {link.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <button
              onClick={() => handleNavClick({ id: 'contact', type: 'section' })}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
            >
              Get Started
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link)}
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium w-full text-left transition-colors duration-200"
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={() => handleNavClick({ id: 'contact', type: 'section' })}
              className="bg-primary-600 hover:bg-primary-700 text-white block px-3 py-2 rounded-lg text-base font-medium w-full text-left mt-4 transition-colors duration-200"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;